import React, { useEffect, useState } from "react";
import UpgradeLicenseWindow from "../layout/UpgradeLicenseWindow";
import { UpgradeLicenseResponse } from "../../models/UpgradeLicenseModel";
import { UpgradeForm } from "../upgrade/UpgradeForm";
import { ConvertedLicense } from "../upgrade/ConvertedLicense";
import { NotEligibleLicense } from "../upgrade/NotEligibleLicense";
import { UpgradeContactForm } from "../upgrade/UpgradeContactForm";
import {LicenseUpgradeService} from "../../utils/Services";
import changeEmailImg from "../../../img/upgrade/change-email.svg";
import Navbar from "../ui/Navbar";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "react-bootstrap";

type ViewState = "form" | "notEligible" | "converted";

interface UpgradeContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    license: string;
    onSuccess: (email: string) => void;
    onError: () => void
}

export const UpgradeContactModal = (props: UpgradeContactModalProps) => {
    const { isOpen, onClose, onSuccess, onError, license } = props;

    return (
        <Modal 
            show={isOpen} 
            onHide={onClose} 
            bsSize="large"
            dialogClassName="modal-dialog-centered show email-change-modal" 
            className="show"
        >
            <ModalHeader className="border-0 pb-0 position-relative">
                    <button
                        onClick={onClose}
                        className="position-absolute top-0 end-0 mt-3 me-3 bg-transparent border-0"
                        style={{ zIndex: 1 }}>
                        <i className="icon-cross"></i>
                    </button>
                    <div className="vstack gap-2 align-items-center">
                    <ModalTitle className="w-100 text-center">
                        <div className="vstack gap-3 align-items-center">
                            <img alt="" className="modal-image" src={changeEmailImg} />
                            <div>
                                <h3 className="modal-title mb-0">Choose another email</h3>
                            </div>
                        </div>
                    </ModalTitle>
                     <p className="text-center px-4 mb-0">
                    If you wish us to contact you using a different email,{" "}
                    <span>please enter it here.</span>
                    </p>
                </div>
            </ModalHeader>
            <ModalBody className="pt-3">
                <UpgradeContactForm
                    onError={onError}
                    onSuccess={onSuccess}
                    license={license}
                />
            </ModalBody>
            <ModalFooter className="border-0 pt-0 justify-content-end gap-3 px-4 pb-4">
                <button
                    onClick={onClose}
                    className="link-button btn text-dark border-0 p-0"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    form="upgradeContactForm"
                    className="btn btn-primary rounded-pill text-white d-flex align-items-center gap-1"
                >
                    <i className="icon-arrow-top"></i>
                    <span>Submit</span>
                </button>
            </ModalFooter>
        </Modal>
    );
};

function useAlternativeStyles() {
    useEffect(() => {
        // Skip style manipulation in Storybook
        if (window.__STORYBOOK_PREVIEW__) {
            return;
        }
        
        const mainStyle = document.getElementById("main_styles");
        const isProduction = !!mainStyle;
        if (isProduction) {
            mainStyle.parentNode.removeChild(mainStyle);

            const newStyle = document.createElement("link");
            newStyle.setAttribute("rel", "stylesheet");
            newStyle.setAttribute("href", "/dist/styles/upgrade.css");
            document.head.appendChild(newStyle);
        } else {
            const styles = Array.from(document.querySelectorAll("style"));
            styles.forEach((style) => {
                style.parentNode.removeChild(style);
            });

            const newStyle = document.createElement("script");
            newStyle.setAttribute("src", "/dev/js/upgrade.js");
            document.head.appendChild(newStyle);
        }
    }, []);
}

const defaultLicenseUpgradeService = new LicenseUpgradeService();

interface UpgradeLicensePageProps {
    licenseUpgradeService?: LicenseUpgradeService;
}

export function UpgradeLicensePage(props: UpgradeLicensePageProps) {
    const { licenseUpgradeService = defaultLicenseUpgradeService } = props;
    const [viewState, setViewState] = useState<ViewState>("form");
    const [error, setError] = useState<string>("");
    const [license, setLicense] = useState<string>("");
    const [targetEmail, setTargetEmail] = useState<string>(null);
    const [didEmailChange, setDidEmailChange] = useState<boolean>(false);
    const [isUpgradeContactErrored, setIsUpgradeContactErrored] = useState(false);
    const [isUpgradeContactModalOpen, setIsUpgradeContactModalOpen] = useState<boolean>(false);

    const ravendbVersion = "7.2";
    
    useAlternativeStyles();

    const onLicenseConverted = (data: UpgradeLicenseResponse) => {
        setTargetEmail(data.email);
        setViewState("converted");
    };

    const onLicenseNotEligible = (data: UpgradeLicenseResponse) => {
        setTargetEmail(data.email);
        setViewState("notEligible");
    };

    const onUpgradeContactModalClose = () => {
        setIsUpgradeContactModalOpen(false);
    };

    const onUpgradeContactModalSuccess = (email: string) => {
        setDidEmailChange(true);
        setTargetEmail(email);
        setIsUpgradeContactModalOpen(false);
        setIsUpgradeContactErrored(false);
    };

    const onUpgradeContactModalError = () => {
        setIsUpgradeContactErrored(true)
        setIsUpgradeContactModalOpen(false)
    };

    return (
        <>
            <UpgradeLicenseWindow>
                <div className="d-flex flex-column w-100">
                    <Navbar />
                {error && <div className="alert bg-danger w-100 text-center active">{error}</div>}
                </div>

                <UpgradeContactModal
                    license={license}
                    isOpen={isUpgradeContactModalOpen}
                    onClose={onUpgradeContactModalClose}
                    onSuccess={onUpgradeContactModalSuccess}
                    onError={onUpgradeContactModalError}
                ></UpgradeContactModal>
                
                {viewState === "converted" && (
                    <ConvertedLicense
                        openUpdateContactModal={() => setIsUpgradeContactModalOpen(true)}
                        email={targetEmail}
                    />
                )}
                
                {viewState === "notEligible" && (
                    <NotEligibleLicense
                        isUpdateContactErrored={isUpgradeContactErrored}
                        didEmailChange={didEmailChange}
                        openUpdateContactModal={() => setIsUpgradeContactModalOpen(true)}
                        email={targetEmail}
                    />
                )}
                
                {viewState === "form" && (
                    <UpgradeForm
                        license={license}
                        setLicense={setLicense}
                        onError={setError}
                        onConverted={onLicenseConverted}
                        onNotEligible={onLicenseNotEligible}
                        licenseUpgradeService={licenseUpgradeService}
                        ravendbVersion={ravendbVersion}
                    />
                )}
                
            </UpgradeLicenseWindow>
        </>
    );
}
